import React from 'react';
import type { LucideIcon } from 'lucide-react';
import styles from './MetricCard.module.css';

export interface MetricCardProps {
  id: string;
  label: string;
  value: number;
  change: string;
  trend: 'up' | 'down' | 'flat';
  timeframe: string;
  icon: LucideIcon;
  accentColor: string;
  format: 'currency' | 'number';
  isActive?: boolean;
  onClick?: (id: string) => void;
  formatCurrency?: (amount: number) => string;
  formatNumber?: (num: number) => string;
}

/**
 * Append alpha channel to hex color
 */
const appendAlpha = (hex: string, alphaHex = '1a'): string => {
  const value = (hex || '').trim();
  if (!value || !value.startsWith('#')) {
    return hex;
  }
  if (value.length === 4) {
    const r = value[1];
    const g = value[2];
    const b = value[3];
    return `#${r}${r}${g}${g}${b}${b}${alphaHex}`;
  }
  if (value.length === 7) {
    return `${value}${alphaHex}`;
  }
  if (value.length === 9) {
    return value;
  }
  return hex;
};

/**
 * Default currency formatter (returns string without decimals)
 */
const defaultFormatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Split formatted currency into major and minor parts for styling
 */
const splitCurrencyParts = (formatted: string): { major: string; minor: string } => {
  // Match pattern: currency symbol + amount + decimal separator + cents
  const match = formatted.match(/^(.*?)([\.,])(\d{2})$/);
  if (!match) {
    return { major: formatted, minor: '' };
  }
  const [, major, separator, cents] = match;
  return { major, minor: `${separator}${cents}` };
};

/**
 * Default number formatter
 */
const defaultFormatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-AU').format(num);
};

/**
 * MetricCard - A reusable metric display card with icon, value, and trend indicator
 * 
 * Features:
 * - Clickable/selectable state with accent color styling
 * - Icon with themed background
 * - Change/trend indicator with timeframe
 * - Formatted value display (currency or number)
 * - Accessible button semantics
 */
export const MetricCard: React.FC<MetricCardProps> = ({
  id,
  label,
  value,
  change,
  trend,
  timeframe,
  icon: Icon,
  accentColor,
  format,
  isActive = false,
  onClick,
  formatCurrency = defaultFormatCurrency,
  formatNumber = defaultFormatNumber
}) => {
  const translucentAccent = appendAlpha(accentColor);

  const handleClick = () => {
    if (onClick) {
      onClick(id);
    }
  };

  // Format the value
  let formattedValue: string | React.ReactNode;
  if (format === 'currency') {
    const currencyString = formatCurrency(value);
    const { major, minor } = splitCurrencyParts(currencyString);
    
    formattedValue = minor ? (
      <>
        {major}
        <span className={styles.metricValueMinor}>{minor}</span>
      </>
    ) : currencyString;
  } else {
    formattedValue = formatNumber(value);
  }

  // For aria-label, use plain text version
  const plainTextValue = format === 'currency'
    ? formatCurrency(value)
    : formatNumber(value);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`${styles.metricCard} ${isActive ? styles.metricCardActive : ''}`}
      style={isActive ? { borderColor: accentColor, background: translucentAccent } : undefined}
      aria-pressed={isActive}
      aria-label={`${label}: ${plainTextValue}`}
    >
      <div className={styles.metricCardTop}>
        <div
          className={styles.metricIcon}
          style={{ color: accentColor, background: translucentAccent }}
        >
          <Icon className={styles.icon} />
        </div>
        <div className={styles.metricChange}>
          <div
            className={`${styles.metricChangeValue} ${
              trend === 'up' ? styles.metricChangeValueUp : styles.metricChangeValueDown
            }`}
          >
            {change}
          </div>
          <div className={styles.metricChangeTimeframe}>{timeframe}</div>
        </div>
      </div>
      <div className={styles.metricContent}>
        <div className={styles.metricValue}>{formattedValue}</div>
        <div className={styles.metricLabel}>{label}</div>
      </div>
    </button>
  );
};
